import { ArrowLeft, Pencil, ScanLine } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CodeBarres } from '../components/CodeBarres.tsx';
import { ApiError } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import type { Licence as LicenceData } from '../lib/licence.ts';
import { COULEURS_DEPART, getLicence, numeroLisible } from '../lib/licence.ts';

/** Une ligne de la carte : l'étiquette au-dessus, la valeur en dessous.
 *
 *  Et non côte à côte. La carte fédérale imprime ses informations en colonne,
 *  au format paysage, ce qui oblige à tourner le téléphone : deux gestes pour
 *  montrer un numéro. À la verticale, chaque information tient sur deux lignes
 *  courtes et la valeur porte tout le poids typographique — c'est elle qu'on
 *  cherche, jamais l'étiquette. */
function Ligne({ mot, valeur }: { mot: string; valeur: string }) {
    return (
        <div>
            <dt className="text-[10px] font-bold uppercase tracking-[0.14em] opacity-45">
                {mot}
            </dt>
            <dd className="m-0 text-base font-semibold leading-snug">{valeur}</dd>
        </div>
    );
}

/* La licence, en carte verticale.
 *
 * ── Ce que cet écran remplace ───────────────────────────────────────────────
 *
 * L'application de la fédération, qu'il fallait ouvrir en plus de celle-ci
 * pour montrer sa licence au starter — alors qu'on a déjà le téléphone en main
 * pour noter sa carte. Les informations sont recopiées à la main, une fois par
 * saison : la fédération n'ouvre aucune interface publique pour les lire, et
 * il n'y a pas de façon honnête d'aller les chercher ailleurs.
 *
 * ── Le code-barres est en HAUT, et grand ────────────────────────────────────
 *
 * C'est la seule chose qu'un tiers regarde. Le reste — nom, club, classement —
 * sert au joueur ou se contrôle à l'œil ; le code, lui, passe sous une
 * douchette, et on tend son téléphone à bout de bras. Il occupe donc toute la
 * largeur et le premier tiers de la hauteur, sur fond blanc franc.
 *
 * ── La luminosité n'est pas forcée ──────────────────────────────────────────
 *
 * Un écran de licence qui monte le rétroéclairage au maximum se voit dans
 * toutes les applications de billetterie, et il vide la batterie d'un joueur
 * qui a quatre heures de parcours devant lui. Le contraste du code — noir pur
 * sur blanc pur, barres larges — suffit aux lecteurs ; c'est le réglage du
 * dessin qui fait la lisibilité, pas la puissance de la lampe.
 */
export function Licence() {
    const { user } = useAuth();
    const [licence, setLicence] = useState<LicenceData | null | undefined>(undefined);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        void getLicence()
            .then(setLicence)
            .catch((e) => {
                setError(e instanceof ApiError ? e.message : 'Chargement impossible');
                setLicence(null);
            });
    }, []);

    if (licence === undefined) {
        return (
            <div className="flex justify-center py-16">
                <span className="loading loading-ring loading-lg text-primary" />
            </div>
        );
    }

    const depart = licence?.teeColour ? COULEURS_DEPART[licence.teeColour] : null;

    return (
        <div className="space-y-4 py-2">
            <Link to="/" className="btn btn-ghost btn-sm gap-2">
                <ArrowLeft size={16} /> Tableau de bord
            </Link>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            {licence == null ? (
                /* Pas de licence saisie : on dit où la saisir, et on ne présente
                   pas une carte vide qui aurait l'air cassée. */
                <section className="rounded-box border border-dashed border-base-300 p-8 text-center">
                    <ScanLine size={32} className="mx-auto opacity-40" aria-hidden="true" />
                    <p className="mt-3 font-semibold">Aucune licence enregistrée</p>
                    <p className="mx-auto mt-1 max-w-xs text-sm opacity-65">
                        Recopiez votre carte fédérale une fois, et vous l’aurez ici même,
                        sans réseau.
                    </p>
                    <Link to="/preferences" className="btn btn-primary mt-4 gap-2">
                        <Pencil size={16} /> Saisir ma licence
                    </Link>
                </section>
            ) : (
                <>
                    <section className="overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-md">
                        {/* Le code, plein cadre. */}
                        <div className="bg-white px-3 pb-2 pt-3">
                            <CodeBarres valeur={licence.number} hauteur={88}
                                className="block h-24 w-full" />
                            <p className="font-score mt-1 text-center text-lg tracking-[0.2em] text-black">
                                {numeroLisible(licence.number)}
                            </p>
                        </div>

                        {/* Le porteur, sur le vert de scène : c'est l'entête de la
                            carte, comme le bandeau d'un tour en cours. */}
                        <header className="bg-[var(--fairway-deep)] px-4 py-3 text-white">
                            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/60">
                                Licence ffgolf
                            </p>
                            <h1 className="font-display truncate text-2xl font-bold leading-tight">
                                {user?.displayName ?? '—'}
                            </h1>
                        </header>

                        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 p-4">
                            {licence.clubName && (
                                <div className="col-span-2">
                                    {/* Le statut est COLLÉ au club, et non sur une
                                        ligne « Statut » à part. Il ne qualifie que
                                        lui : « abonné » tout seul, deux lignes plus
                                        bas, ne disait pas abonné de quoi. Et les deux
                                        états s'écrivent désormais — rien afficher pour
                                        un indépendant faisait passer une information
                                        pour une absence de saisie. */}
                                    <Ligne mot={licence.subscriber
                                        ? 'Club — abonné' : 'Club — indépendant rattaché'}
                                        valeur={licence.clubNumber
                                            ? `${licence.clubName} — ${licence.clubNumber}`
                                            : licence.clubName} />
                                </div>
                            )}
                            {licence.category && <Ligne mot="Catégorie" valeur={licence.category} />}
                            {licence.season && <Ligne mot="Saison" valeur={licence.season} />}
                            {/* L'index vient du profil, jamais de la carte : la
                                fédération l'appelle « niveau de jeu », mais c'est
                                le même nombre que celui des pastilles d'avatar.
                                Deux copies finiraient par diverger. */}
                            {user?.handicapIndex != null && (
                                <Ligne mot="Niveau de jeu"
                                    valeur={user.handicapIndex.toLocaleString('fr-FR', {
                                        minimumFractionDigits: 1,
                                    })} />
                            )}
                            {licence.ranking && <Ligne mot="Classement" valeur={licence.ranking} />}

                            {depart && (
                                <div className="col-span-2">
                                    <dt className="text-[10px] font-bold uppercase tracking-[0.14em] opacity-45">
                                        Départ recommandé
                                    </dt>
                                    <dd className="m-0 mt-1">
                                        {/* La couleur EST l'information : le mot
                                            seul obligerait à lire là où une
                                            pastille se reconnaît de loin. Le mot
                                            reste, pour qui ne distingue pas le
                                            rouge du vert. */}
                                        <span className="inline-flex items-center gap-2 rounded-full px-3 py-1
                                                         text-sm font-bold ring-1 ring-black/15"
                                            style={{ background: depart.pigment, color: depart.encre }}>
                                            {depart.mot}
                                        </span>
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </section>

                    <Link to="/preferences" className="btn btn-ghost btn-sm gap-2">
                        <Pencil size={16} /> Corriger ma licence
                    </Link>
                </>
            )}
        </div>
    );
}
